import type { NearbyOrganizationsResponse } from "./types/nearby-organization";

import { mapOfficesToOrganizations } from "./map-offices-to-organizations";
import { searchHiringOffices } from "./search-hiring-offices";

type FetchHiringGeoSearchOptions = {
  onError?: (error: unknown, message: string) => void | Promise<void>;
};

function parseHiringGeoSearchParams(searchParams: URLSearchParams) {
  const location = searchParams.get("query");
  const radius = Number.parseInt(searchParams.get("miles") || "30", 10);
  const forceProximitySearch = searchParams.has("forceProximitySearch")
    ? searchParams.get("forceProximitySearch") === "true"
    : searchParams.has("miles");

  return { forceProximitySearch, location, radius };
}

async function fetchHiringOrganizations(
  searchParams: URLSearchParams,
): Promise<NearbyOrganizationsResponse> {
  const { forceProximitySearch, location, radius } =
    parseHiringGeoSearchParams(searchParams);

  if (!location) {
    throw new Error("Missing location parameter");
  }

  const { offices } = await searchHiringOffices(
    {
      forceProximitySearch,
      geoSearchTerm: location,
      mileRadius: radius,
    },
    { throwOnError: true },
  );

  return mapOfficesToOrganizations(offices);
}

async function fetchHiringGeoSearch(
  request: Request,
  options: FetchHiringGeoSearchOptions = {},
): Promise<Response> {
  try {
    const data = await fetchHiringOrganizations(
      new URL(request.url).searchParams,
    );
    return Response.json({ data, success: true });
  } catch (error) {
    if (
      error instanceof Error &&
      error.message === "Missing location parameter"
    ) {
      return Response.json(
        { message: "Missing location parameter", success: false },
        { status: 400 },
      );
    }

    console.error("Error fetching locations:", error);
    const message = `Error fetching locations: ${error}`;
    await options.onError?.(error, message);

    return Response.json(
      {
        message: error instanceof Error ? error.message : "Server error",
        success: false,
      },
      { status: 500 },
    );
  }
}

export { fetchHiringGeoSearch, fetchHiringOrganizations };
